import React, { useState, useEffect } from "react";
import { Grid } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import "./qrcodeinnerpage.scss";
import SeqHeader from "./seqHeader";
import SelectMenu from "../../library/custom/selectMenu/SelectMenu";
import FormLabels from "../../library/custom/formLabel/FormLabels";
import DatePickers from "../../library/custom/datePicker/DatePicker";
import GridTable from "../../library/custom/gridTable/GridTable";
import { TableLoader } from "../../library/common/Loader";
import NORow from "../../library/custom/noRow/noRow";
import QrBackBtn from "../../library/custom/qrBackBtn/qrBackBtn";
import DonoughtChart from "../../feature/support/component/ticket/charts";
import RainFallDataPerHour from "../../feature/storm-water/admin/components/rainFallDataPerHour";
import { FILTER_RAINFALL_DAYS } from "../../constant/constant";
import { useThemeContext } from "../../context";
import tableCancel from "../../assets/sw_error.svg";
import tableImgDark from "../../assets/humidity_mid_dark.svg";
import {
  formatRainfallValue,
  getFormatedDate,
  parseDate,
  twoDigitAfterDecimal,
} from "../../utils/helper";

const PAGE_SIZE_30 = 30;
const RAINFALL_TYPE_HISTORICAL = 1;
const CUSTOM_RANGE_KEY = 12;

const SequenceRainfallData = ({
  rainfallData,
  getSequenceRainfallData,
  projectId,
  clientName,
  projectName,
  projectNumber,
}) => {
  const [page, setPage] = useState(0);
  const [rainFallDateRange, setRainFallDateRange] = useState(30);
  // Store the picker selection as Date objects; format to a string only at the
  // API boundary. Keeping strings here broke the range guard (lexicographic
  // compare) and fed string min/value props into MUI's date adapter.
  const [formDate, setFormDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [reversedData, setReversedData] = useState([]);

  const { mode } = useThemeContext();
  const [open, setOpen] = useState(false);
  const [hourData, setHourData] = useState();
  const [isHourDataLoading, setIsHourDataLoading] = useState(false);
  const [currentAccountId, setCurrentAccountId] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);
  const handleClose = () => setOpen(false);

  const historicalData = rainfallData?.data?.historyPrecipList;
  const historicalDataCount = rainfallData?.data?.historyPrecipCount;
  const isLoading = rainfallData?.isLoading;

  const today = new Date();
  const lastThreemonthDate = new Date(today);
  lastThreemonthDate.setDate(today.getDate() - 90);

  const fetchRainfall = (pageNo, from, to) => {
    getSequenceRainfallData({
      projectId: projectId,
      rainfallType: RAINFALL_TYPE_HISTORICAL,
      page: pageNo,
      pageSize: PAGE_SIZE_30,
      fromDate: from ? getFormatedDate(from) : "",
      toDate: to ? getFormatedDate(to) : "",
    });
  };

  // Initialize the default 30-day range on mount.
  useEffect(() => {
    if (rainFallDateRange === 30 && !formDate && !toDate) {
      const calculated = new Date(today);
      calculated.setDate(today.getDate() - (30 - 1));
      setFormDate(calculated);
      setToDate(today);
    }
  }, []);

  // Refetch whenever a valid range is selected.
  useEffect(() => {
    if (formDate && toDate && formDate <= toDate) {
      setPage(0);
      fetchRainfall(1, formDate, toDate);
    }
  }, [formDate, toDate]);

  useEffect(() => {
    if (Array.isArray(historicalData)) {
      setReversedData([...historicalData].reverse());
    }
  }, [rainfallData]);

  const onChangeDateRange = (e) => {
    const value = e?.target?.value;
    setRainFallDateRange(value);
    if (value !== CUSTOM_RANGE_KEY) {
      const days = parseInt(value);
      const calculated = new Date(today);
      calculated.setDate(today.getDate() - (days - 1));
      setFormDate(calculated);
      setToDate(today);
    } else {
      setFormDate(null);
      setToDate(null);
    }
  };

  const onChangeFromDate = (e) => setFormDate(e);
  const onChangeToDate = (e) => setToDate(e);

  const handlePagination = (newPage) => {
    setPage(newPage);
    fetchRainfall(newPage + 1, formDate, toDate);
  };

  const fetchHourData = async (accountId, date) => {
    if (!accountId || !date) return;
    setIsHourDataLoading(true);
    setHourData(null);
    setCurrentDate(date);
    try {
      const response = await fetch(
        `https://rainfall.waynetech.space/api/v1/precip/${accountId}/24-hr-data?date=${date}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            "X-API-Key": "UYThQwfar9VTYA",
          },
        },
      );
      const data = await response.json();
      setHourData(data);
    } catch (error) {
      console.error("Error fetching 24-hr rainfall data:", error);
      setHourData(null);
    } finally {
      setIsHourDataLoading(false);
    }
  };

  const handlePrevDay = () => {
    if (!currentDate || !currentAccountId) return;
    const d = new Date(currentDate);
    d.setDate(d.getDate() - 1);
    const prevDate = d.toISOString().split("T")[0];
    fetchHourData(currentAccountId, prevDate);
  };

  const handleNextDay = () => {
    if (!currentDate || !currentAccountId) return;
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 1);
    const nextDate = d.toISOString().split("T")[0];
    fetchHourData(currentAccountId, nextDate);
  };

  const handleIconClick = (event, row) => {
    setOpen(true);
    const accountId = row?.row?.accountId;
    const dateParts = row?.row?.precipDateFormat?.split("-");
    const date = dateParts
      ? `${dateParts[2]}-${dateParts[0]}-${dateParts[1]}`
      : null;
    setCurrentAccountId(accountId);
    console.log(accountId);
    fetchHourData(accountId, date);
  };

  const columns = [
    {
      field: "projectNumber",
      headerName: "Project Number",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "precipDate",
      headerName: "Date",
      flex: 1,
      minWidth: 120,
      renderCell: (cellValues) => {
        const formattedDate = parseDate(
          cellValues?.row?.precipDateFormat,
          "MM-dd-yyyy",
        );
        const options = { year: "numeric", month: "2-digit", day: "2-digit" };
        const formattedDateString = formattedDate
          ? formattedDate.toLocaleDateString("en-US", options)
          : null;
        return (
          <div className="nameBox1">
            <div className="nameImg1">
              <p>
                {cellValues ? (
                  <div className="checksample">{formattedDateString}</div>
                ) : (
                  "N/A"
                )}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      field: "evenTrackPrecipAmt",
      headerName: "Rain Amount (Inches)",
      flex: 1.3,
      minWidth: 150,
      renderCell: (rows) => (
        <div className="swdashCustomer_rainfallEvnt">
          <div className="rfEventAmt_div">
            {formatRainfallValue(rows?.row?.evenTrackPrecipAmt)}
          </div>
          <div
            className="sw-sample-icon_tool_tip"
            onClick={(e) => handleIconClick(e, rows)}
          >
            <div>
              <img src={mode === "dark" ? tableImgDark : tableCancel} alt="" />
            </div>
          </div>
        </div>
      ),
    },
    { field: "exceedance", headerName: "Exceedence", flex: 0.8, minWidth: 100 },
  ];

  return (
    <>
      <RainFallDataPerHour
        open={open}
        handleClose={handleClose}
        hourData={hourData}
        isLoading={isHourDataLoading}
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        currentDate={currentDate}
      />
      <SeqHeader
        clientName={clientName}
        ProjectName={projectName}
        ProjectNumber={projectNumber}
      />

      <section className="grid_main_sec">
        <div className="gridMainpermit">
          <Grid className="permitBox" lg={12} mb={2}>
            <Grid className="grid_main_header two_grid" lg={12}>
              <Grid lg={8} className="grid_heading_style" space={2}>
                Historical Rainfall Data
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={3}>
            <Grid item lg={4} xs={12} sm={12} md={6}>
              <FormControl fullWidth>
                <FormLabels label="Date Range" isRequired={false} />
                <SelectMenu
                  listData={FILTER_RAINFALL_DAYS}
                  value={rainFallDateRange}
                  onchangehandler={(e) => onChangeDateRange(e)}
                  placeholder="Please Select"
                />
              </FormControl>
            </Grid>

            {rainFallDateRange === CUSTOM_RANGE_KEY && (
              <>
                <Grid item lg={4} xs={12} sm={12} md={6}>
                  <FormControl fullWidth>
                    <FormLabels label={"From"} isRequired={true} />
                    <DatePickers
                      maxDateEnable={false}
                      value={formDate}
                      disabled={false}
                      onChangeHandler={(e) => onChangeFromDate(e)}
                      minDate={lastThreemonthDate}
                      forceDesktop
                    />
                  </FormControl>
                </Grid>
                <Grid item lg={4} xs={12} sm={12} md={6}>
                  <FormControl fullWidth>
                    <FormLabels label={"To"} isRequired={true} />
                    <DatePickers
                      maxDateEnable={false}
                      minDate={formDate}
                      value={toDate}
                      disabled={false}
                      onChangeHandler={(e) => onChangeToDate(e)}
                      forceDesktop
                    />
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>

          <Grid container mb={3}>
            <Grid item lg={12} xs={12}>
              <DonoughtChart LineChart datahistoricalData={reversedData} />
            </Grid>
          </Grid>

          <div className="desc_box row_uniq">
            <div className="table_style_ui">
              <GridTable
                getRowId={(r) => r?.precipId}
                rows={historicalData ?? []}
                rowCount={historicalDataCount ?? 0}
                columns={columns}
                loading={isLoading}
                checkboxSelection={false}
                page={page}
                pagination
                pageSize={PAGE_SIZE_30}
                rowsPerPageOptions={[PAGE_SIZE_30]}
                onPageChange={handlePagination}
                backIconButtonProps={{ disabled: false }}
                disableSelectionOnClick
                components={{
                  LoadingOverlay: TableLoader,
                  NoRowsOverlay: NORow,
                }}
              />
            </div>
          </div>

          <Grid container>
            <QrBackBtn text={"Back"} />
          </Grid>
        </div>
      </section>
    </>
  );
};

export default SequenceRainfallData;
